export type NestSheet = { widthMm: number; heightMm: number };
export type NestItem = { id: string; widthMm: number; heightMm: number; qty: number };
export type NestOptions = { gapMm: number; allowRotate: boolean; marginMm?: number };
export type NestPlacement = {
  id: string;
  copyIndex: number;
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  rotated: boolean;
};
export type NestResult = {
  placed: NestPlacement[];
  unplaced: Array<{ id: string; count: number }>;
  fillRatio: number;
};

type FreeRect = { x: number; y: number; w: number; h: number };
type Piece = { id: string; copyIndex: number; w: number; h: number };
type Fit = { rect: FreeRect; rotated: boolean; shortSideFit: number; longSideFit: number };

// Tolerance for float comparisons so touching edges are not treated as overlaps.
const EPS = 1e-9;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function compareIds(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  return a > b ? 1 : 0;
}

// Best Short Side Fit: score a piece of pw×ph against a free rect.
// Returns null when the piece does not fit.
function scoreFit(rect: FreeRect, pw: number, ph: number): { shortSideFit: number; longSideFit: number } | null {
  const leftoverW = rect.w - pw;
  const leftoverH = rect.h - ph;
  if (leftoverW < -EPS || leftoverH < -EPS) {
    return null;
  }
  return {
    shortSideFit: Math.min(leftoverW, leftoverH),
    longSideFit: Math.max(leftoverW, leftoverH),
  };
}

// Pick the free rect (and orientation) with the smallest short side leftover,
// breaking ties by long side leftover. Strict comparisons keep the choice
// deterministic: earlier free rects and the non-rotated orientation win ties.
function findBestFit(freeRects: FreeRect[], piece: Piece, allowRotate: boolean): Fit | null {
  let best: Fit | null = null;

  freeRects.forEach((rect) => {
    const orientations: Array<{ w: number; h: number; rotated: boolean }> = [
      { w: piece.w, h: piece.h, rotated: false },
    ];
    if (allowRotate && piece.w !== piece.h) {
      orientations.push({ w: piece.h, h: piece.w, rotated: true });
    }

    orientations.forEach(({ w, h, rotated }) => {
      const score = scoreFit(rect, w, h);
      if (!score) {
        return;
      }
      if (
        !best ||
        score.shortSideFit < best.shortSideFit - EPS ||
        (Math.abs(score.shortSideFit - best.shortSideFit) <= EPS && score.longSideFit < best.longSideFit - EPS)
      ) {
        best = { rect, rotated, ...score };
      }
    });
  });

  return best;
}

// Split every free rect intersected by the placed rect into up to four
// maximal leftovers, then drop free rects fully contained in another.
function splitFreeRects(freeRects: FreeRect[], placed: FreeRect): FreeRect[] {
  const next: FreeRect[] = [];

  freeRects.forEach((rect) => {
    const intersects =
      placed.x < rect.x + rect.w - EPS &&
      placed.x + placed.w > rect.x + EPS &&
      placed.y < rect.y + rect.h - EPS &&
      placed.y + placed.h > rect.y + EPS;

    if (!intersects) {
      next.push(rect);
      return;
    }

    const leftW = placed.x - rect.x;
    if (leftW > EPS) {
      next.push({ x: rect.x, y: rect.y, w: leftW, h: rect.h });
    }
    const rightX = placed.x + placed.w;
    if (rect.x + rect.w - rightX > EPS) {
      next.push({ x: rightX, y: rect.y, w: rect.x + rect.w - rightX, h: rect.h });
    }
    const topH = placed.y - rect.y;
    if (topH > EPS) {
      next.push({ x: rect.x, y: rect.y, w: rect.w, h: topH });
    }
    const bottomY = placed.y + placed.h;
    if (rect.y + rect.h - bottomY > EPS) {
      next.push({ x: rect.x, y: bottomY, w: rect.w, h: rect.y + rect.h - bottomY });
    }
  });

  // Prune rects contained in others so the list stays maximal.
  return next.filter(
    (a, i) =>
      !next.some(
        (b, j) =>
          j !== i &&
          (j < i || !(b.x === a.x && b.y === a.y && b.w === a.w && b.h === a.h)) &&
          b.x <= a.x + EPS &&
          b.y <= a.y + EPS &&
          b.x + b.w >= a.x + a.w - EPS &&
          b.y + b.h >= a.y + a.h - EPS,
      ),
  );
}

// MaxRects (Best Short Side Fit) nesting of rectangular pieces on a single sheet.
// Pure and deterministic: the same input always yields the identical result.
// Pieces are packed with gapMm padding so any two pieces end up ≥ gapMm apart
// and every piece stays ≥ marginMm away from the sheet edges.
function nestOnSheet(sheet: NestSheet, items: NestItem[], options: NestOptions): NestResult {
  const gap = options.gapMm;
  const margin = options.marginMm ?? 0;

  // Padding each piece by gap on the right/bottom and extending the inner
  // sheet by the same gap makes the outermost row need no trailing gap.
  const innerW = sheet.widthMm - 2 * margin + gap;
  const innerH = sheet.heightMm - 2 * margin + gap;

  const pieces: Piece[] = [];
  items.forEach((item) => {
    for (let copyIndex = 0; copyIndex < item.qty; copyIndex += 1) {
      pieces.push({ id: item.id, copyIndex, w: item.widthMm + gap, h: item.heightMm + gap });
    }
  });

  // Big pieces first; id/copyIndex tie-breaks keep the order fully stable.
  pieces.sort((a, b) => b.w * b.h - a.w * a.h || compareIds(a.id, b.id) || a.copyIndex - b.copyIndex);

  let freeRects: FreeRect[] = innerW > EPS && innerH > EPS ? [{ x: 0, y: 0, w: innerW, h: innerH }] : [];
  const placed: NestPlacement[] = [];
  const unplacedById = new Map<string, number>();
  let placedArea = 0;

  pieces.forEach((piece) => {
    const fit = findBestFit(freeRects, piece, options.allowRotate);
    if (!fit) {
      unplacedById.set(piece.id, (unplacedById.get(piece.id) ?? 0) + 1);
      return;
    }

    const pw = fit.rotated ? piece.h : piece.w;
    const ph = fit.rotated ? piece.w : piece.h;
    const placedRect: FreeRect = { x: fit.rect.x, y: fit.rect.y, w: pw, h: ph };
    freeRects = splitFreeRects(freeRects, placedRect);

    const realW = pw - gap;
    const realH = ph - gap;
    placed.push({
      id: piece.id,
      copyIndex: piece.copyIndex,
      xMm: round2(margin + placedRect.x),
      yMm: round2(margin + placedRect.y),
      widthMm: round2(realW),
      heightMm: round2(realH),
      rotated: fit.rotated,
    });
    placedArea += realW * realH;
  });

  const sheetArea = sheet.widthMm * sheet.heightMm;

  return {
    placed,
    unplaced: Array.from(unplacedById, ([id, count]) => ({ id, count })),
    fillRatio: sheetArea > 0 ? placedArea / sheetArea : 0,
  };
}

export default nestOnSheet;
